import React from 'react';
import {Button, IconButton, TextField, InputAdornment, Box, Typography} from '@mui/material';
import {Search, Add, ControlPointDuplicate, DeleteOutline, Rule} from '@mui/icons-material';
import {useTrovoConfig} from '../utils/config';

interface AddSampleButtonProps {
    addClick: (event: boolean) => void;
    disabled: boolean;
}

const AddSampleButton = ({addClick, disabled}: AddSampleButtonProps) => (
    <Button
        onClick={() => addClick(true)}
        variant="contained"
        color="primary"
        startIcon={<Add />}
        sx={{borderRadius: '5px', marginRight: '5px'}}
        disabled={disabled}>
        Add Sample
    </Button>
);

interface FilterButtonProps {
    showFilters: any;
    setShowFilters: any;
}

const FilterButton = ({showFilters, setShowFilters}: FilterButtonProps) => (
    <Button
        color="primary"
        aria-label="filter"
        onClick={e => setShowFilters(showFilters ? null : e.currentTarget)}
        startIcon={<Rule />}
        sx={{
            border: '1px solid',
            borderRadius: '5px',
            borderColor: 'outline-variant',
            mx: 1,
            bgcolor: showFilters ? 'surface-container-highest' : ''
        }}>
        Filter
    </Button>
);

interface SearchInputProps {
    onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const SearchInput = ({onSearchChange}: SearchInputProps) => (
    <TextField
        label="Search"
        variant="outlined"
        size="small"
        onChange={onSearchChange}
        InputProps={{
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton>
                        <Search />
                    </IconButton>
                </InputAdornment>
            )
        }}
    />
);

interface DeleteButtonProps {
    onClick: () => void;
    disabled: boolean;
}

const DeleteButton = ({onClick, disabled}: DeleteButtonProps) => (
    <Button
        color="primary"
        aria-label="delete"
        size="small"
        startIcon={<DeleteOutline />}
        sx={{border: '1px solid', borderRadius: '5px', borderColor: 'outline-variant', py: '4px', mx: 1}}
        disabled={disabled}
        onClick={onClick}
        data-testid={'delete-button'}>
        Delete
    </Button>
);

interface DuplicationButtonProps {
    onClick: () => void;
    disabled: boolean;
}

const DuplicationButton = ({onClick, disabled}: DuplicationButtonProps) => (
    <Button
        onClick={onClick}
        color="primary"
        aria-label="duplicate"
        size="small"
        startIcon={<ControlPointDuplicate />}
        sx={{border: '1px solid', borderRadius: '5px', borderColor: 'outline-variant', py: '4px'}}
        disabled={disabled}>
        Duplicate
    </Button>
);

interface ButtonRowProps {
    areSelectedSamples: boolean;
    onSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    addClick?: (event: boolean) => void;
    onDelete?: () => void;
    setDuplicationStarted: (event: boolean) => void;
    setShowFilters?: any;
    showFilters?: any;
}

export const ButtonRow: React.FC<ButtonRowProps> = ({
    areSelectedSamples,
    onSearchChange,
    addClick,
    onDelete,
    setDuplicationStarted,
    setShowFilters,
    showFilters
}) => {
    const {user} = useTrovoConfig();
    const isDemoAccount: boolean = user.cb_item_price_id?.includes('demo');
    const onDuplicateHandle = () => setDuplicationStarted(true);

    return (
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0'}}>
            <Box>
                {addClick && <AddSampleButton addClick={addClick} disabled={isDemoAccount} />}
                {setShowFilters && <FilterButton showFilters={showFilters} setShowFilters={setShowFilters} />}
                {onSearchChange && <SearchInput onSearchChange={onSearchChange} />}
            </Box>

            <Box>
                {onDelete && <DeleteButton onClick={onDelete} disabled={isDemoAccount || !areSelectedSamples} />}
                {onDuplicateHandle && <DuplicationButton onClick={onDuplicateHandle} disabled={isDemoAccount || !areSelectedSamples} />}
            </Box>
        </Box>
    );
};
